import axios from 'axios';

const TOKEN_KEY = 'mu.auth.token';
const ORG_KEY = 'mu.auth.currentOrg';

// Token puente para el "learn nuevo" (learn-app-next) embebido via iframe
// same-origin. El learn-app lo lee de la cookie `mu_bridge_token` (ver
// learn-app/app/Support/RootApi/CurrentRootToken.php). Al ser el MISMO origen
// (new.measureup.com), la cookie viaja tanto en la navegacion top-level como en
// el iframe. Se escribe/borra junto con el PAT de Sanctum en saveToken().
const BRIDGE_COOKIE = 'mu_bridge_token';

function writeBridgeCookie(token) {
  try {
    const secure = (typeof location !== 'undefined' && location.protocol === 'https:') ? '; Secure' : '';
    if (token) {
      // SameSite=Lax basta para same-origin (no es cross-site). Path raiz para
      // que cubra /training-library, /report, /badge, etc.
      document.cookie = `${BRIDGE_COOKIE}=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secure}`;
    } else {
      document.cookie = `${BRIDGE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    }
  } catch (_e) { /* SSR / cookies deshabilitadas */ }
}

export function loadToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch (_e) { return null; }
}
export function saveToken(token) {
  try { token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY); } catch (_e) {}
  // Mantener sincronizada la cookie puente para el iframe del learn nuevo.
  writeBridgeCookie(token);
}
export function loadActiveOrg() {
  try { return localStorage.getItem(ORG_KEY) || null; } catch (_e) { return null; }
}
export function saveActiveOrg(orgId) {
  try { orgId != null ? localStorage.setItem(ORG_KEY, String(orgId)) : localStorage.removeItem(ORG_KEY); } catch (_e) {}
}

/**
 * Creates a configured axios instance for the SPA. The base URL defaults to
 * '/api' (Laravel serves SPA + API from the same origin) and can be
 * overridden with VUE_APP_API_BASE_URL at build time.
 *
 * - Injects `Authorization: Bearer <token>` automatically.
 * - On 401 it dispatches `auth/logout` to the bound store and pushes the
 *   router to the configured loginRoute.
 *
 * Use `bindAuth(http, store, router)` once both are constructed to break the
 * circular dependency between http <-> store.
 */
export function createHttp({ loginRoute = '/login' } = {}) {
  // Empty baseURL by default so views can hit absolute paths like `/api/...`.
  // Override via VUE_APP_API_BASE_URL for cross-origin SPAs (eg. a CDN-hosted
  // SPA pointing at api.measureup.com).
  const baseURL = process.env.VUE_APP_API_BASE_URL || '';

  const http = axios.create({
    baseURL,
    withCredentials: false,
    headers: { Accept: 'application/json' },
  });

  http._auth = { store: null, router: null, loginRoute };

  http.interceptors.request.use((config) => {
    const token = loadToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = 'Bearer ' + token;
    }
    // Multi-org: send the active organization as explicit context for the
    // ecosystem. The server still treats the token's org as authoritative.
    const orgId = loadActiveOrg();
    if (orgId) {
      config.headers = config.headers || {};
      config.headers['X-Organization-Id'] = orgId;
    }
    return config;
  });

  http.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error && error.response && error.response.status;
      const url = (error && error.config && error.config.url) || '';
      // A 401 on the auth endpoints is an expected credential/login failure —
      // let the caller handle it and don't tear down the (non-existent) session.
      const isAuthCall = /\/api\/auth\/(login|oauth|register|forgot|reset)/.test(url);
      if (status === 401 && !isAuthCall) {
        const { store, router, loginRoute: lr } = http._auth;
        const hadToken = !!loadToken();
        if (store) store.dispatch('auth/logout', { silent: true });
        if (router && router.currentRoute && router.currentRoute.path !== lr) {
          // BR-002: if we had a token and got kicked, it's most likely because the
          // session was displaced from another device — tell the login screen.
          const query = hadToken ? { reason: 'session_ended' } : undefined;
          router.push(query ? { path: lr, query } : lr).catch(() => {});
        }
      }
      return Promise.reject(error);
    },
  );

  return http;
}

/**
 * Late-binds the store and router used by the http 401 interceptor. Call
 * this once both have been created (typically in main.js after `new Vue`).
 */
export function bindAuth(http, { store, router, loginRoute } = {}) {
  if (!http || !http._auth) return;
  if (store) http._auth.store = store;
  if (router) http._auth.router = router;
  if (loginRoute) http._auth.loginRoute = loginRoute;
}
